'use strict';

import {partial} from 'underscore';

/**
 * Ships logs to the backend to be logged and to generate metrics.
 *
 * This is very similar to ContextualLogger over in lib-lambda, but without the correlation and namespace functions.
 */
function Logger(analyticsStore) {
    /**
     * Valid logging levels
     */
    const LoggingLevels = {
        TRACE: "trace",
        DEBUG: "debug",
        INFO: "info",
        WARN: "warn",
        ERROR: "error",
        FATAL: "fatal"
    }

    /**
     * A map of logging levels with ascending numeric values. Logging methods can use this to determine if
     * they should log or not.
     */
    let NumericLoggingLevels = {
        TRACE: 0,
        DEBUG: 1,
        INFO: 2,
        WARN: 3,
        ERROR: 4,
        FATAL: 5
    }

    let _loggingLevel = LoggingLevels.INFO;

    /**
     * Compares the requested log level to the configured logging level and determines if a log entry should be created
     * or not.
     *
     * @param requestedLevel The requested logging level.
     * @private
     */
    function _shouldLog(requestedLevel) {
        let requestedLevelNumber = NumericLoggingLevels[requestedLevel.toUpperCase()];
        let configuredLevelNumber = NumericLoggingLevels[_loggingLevel.toUpperCase()];
        return requestedLevelNumber >= configuredLevelNumber;
    }

    /*
     * Log  at the given level (if appropriate)
     */
    function _logAtLevel(level, logFunc, message, additionalData, expected) {
        if (_shouldLog(level)) {
            // Add to analytics ...
            const entry = analyticsStore.addError(level, message, additionalData, expected);
            // Log to the console ...
            logFunc(entry);
        }
    }

    const fatal = partial(_logAtLevel, LoggingLevels.FATAL, console.fatal);
    const error = partial(_logAtLevel, LoggingLevels.ERROR, console.error);
    const warn = partial(_logAtLevel, LoggingLevels.WARN, console.warn);
    const info = partial(_logAtLevel, LoggingLevels.INFO, console.info);
    const log = info; // Just a pass though to the info level logger.
    const debug = partial(_logAtLevel, LoggingLevels.DEBUG, console.debug);
    const trace = partial(_logAtLevel, LoggingLevels.TRACE, console.trace);

    /**
     * Sets the logging level
     * @param level One of the level defined in LoggingLevels.
     */
    function setLoggingLevel(level) {
        if (level && level.toUpperCase() in LoggingLevels) {
            _loggingLevel = level;
        } else {
            console.warn(`Requested logging level [${level}] invalid. Valid level are in the constant LoggingLevels.`);
            _loggingLevel = LoggingLevels.INFO;
        }
    }

    /**
     * Returns the current logging level.
     * @return {string} An element of LoggingLevel.
     */
    function getLoggingLevel() {
        return _loggingLevel;
    }

    /*
     * Each of these is a function that returns true if the configured log
     * level is equal to or lower than the checked level
     */
    const isTrace = partial(_shouldLog, LoggingLevels.TRACE);
    const isDebug = partial(_shouldLog, LoggingLevels.DEBUG);
    const isInfo = partial(_shouldLog, LoggingLevels.INFO);
    const isWarn = partial(_shouldLog, LoggingLevels.WARN);
    const isError = partial(_shouldLog, LoggingLevels.ERROR);
    const isFatal = partial(_shouldLog, LoggingLevels.FATAL);

    return Object.freeze({
        LoggingLevels,
        trace,
        debug,
        info,
        warn,
        error,
        fatal,
        log,
        setLoggingLevel,
        getLoggingLevel,
        isTrace,
        isDebug,
        isInfo,
        isWarn,
        isError,
        isFatal,
        // There are internal methods but are exported because they need to be tested.
        _shouldLog
    });
}
export default Logger;
